import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Globe, Package, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta, offeringPrice, typeLabel, eur } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { OfferingCard } from "@/components/marketing/OfferingCard";
import { initials } from "@/lib/format";

export default function OfferingDetail() {
  const { id } = useParams();
  const { isAuthed } = useAuth();
  const t = useT();
  const [offering, setOffering] = useState(undefined); // undefined=loading, null=notfound

  useEffect(() => {
    window.scrollTo(0, 0);
    setOffering(undefined);
    api.get(`/public/offerings/${id}`)
      .then((r) => setOffering(r.data?.error ? null : r.data))
      .catch(() => setOffering(null));
  }, [id]);

  usePageMeta(offering?.title || t("od.fallback_title"), offering?.description);

  if (offering === undefined) {
    return (
      <div className="container-page py-12">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-6 aspect-[16/7] w-full rounded-2xl" />
        <Skeleton className="mt-6 h-10 w-2/3" />
      </div>
    );
  }
  if (offering === null) {
    return (
      <div className="container-page py-20">
        <EmptyState
          icon={Package}
          title={t("od.notfound_title")}
          description={t("od.notfound_desc")}
          action={<Button className="rounded-full" asChild><Link to="/marketplace">{t("od.to_marketplace")}</Link></Button>}
        />
      </div>
    );
  }

  const coach = offering.coach || {};

  return (
    <div className="container-page py-8">
      <nav className="mb-6 flex items-center gap-1 text-sm text-muted-foreground">
        <Link to="/marketplace" className="inline-flex items-center gap-1 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("od.marketplace")}
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="line-clamp-1 text-foreground">{offering.title}</span>
      </nav>

      <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <div className="relative aspect-[16/8] overflow-hidden rounded-2xl bg-oat-200">
            {offering.cover_image_url ? (
              <img src={offering.cover_image_url} alt={offering.title} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-accent"><Package className="h-10 w-10 opacity-40" /></div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Badge className="rounded-full">{typeLabel(offering)}</Badge>
            {offering.promoted ? <Badge className="rounded-full bg-clay text-white hover:bg-clay">{t("od.recommended")}</Badge> : null}
            {(offering.tags || []).map((t) => (
              <Badge key={t} variant="secondary" className="rounded-full font-normal">{t}</Badge>
            ))}
          </div>

          <h1 className="mt-4 font-display text-3xl text-foreground sm:text-4xl">{offering.title}</h1>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
            {offering.duration_minutes ? <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {t("od.minutes", { n: offering.duration_minutes })}</span> : null}
            {offering.delivery ? <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4" /> {offering.delivery}</span> : null}
            {offering.region ? <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {offering.region}</span> : null}
          </div>

          <div className="mt-7 whitespace-pre-line text-[15px] leading-relaxed text-foreground/90">
            {offering.description_full || offering.description}
          </div>

          {offering.bundle_items?.length ? (
            <div className="mt-8">
              <h3 className="font-display text-xl text-foreground">{t("od.included")}</h3>
              <div className="mt-3 space-y-2">
                {offering.bundle_items.map((it) => (
                  <div key={it.id} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                    <span className="text-sm text-foreground">{it.title}</span>
                    <span className="text-sm text-muted-foreground">{eur(it.price_cents, it.currency || "EUR")}</span>
                  </div>
                ))}
              </div>
              {offering.bundle_savings_cents > 0 && (
                <p className="mt-3 text-sm text-primary">{t("od.savings", { amount: eur(offering.bundle_savings_cents) })}</p>
              )}
            </div>
          ) : null}
        </div>

        {/* Sticky side panel */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground">{t("od.price")}</p>
            <p className="font-display text-3xl font-semibold text-primary">{offeringPrice(offering)}</p>

            <Link to={`/coaches/${coach.id}`} className="mt-6 flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:bg-accent">
              <Avatar className="h-11 w-11">
                <AvatarImage src={coach.photo} alt={coach.name} />
                <AvatarFallback className="bg-primary text-primary-foreground">{initials(coach.name)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{coach.name}</p>
                {coach.headline ? <p className="truncate text-xs text-muted-foreground">{coach.headline}</p> : null}
              </div>
            </Link>

            <Button className="mt-5 w-full rounded-full" size="lg" asChild data-testid="offering-coach-cta">
              <Link to={`/coaches/${coach.id}`}>{t("od.cta")}</Link>
            </Button>
            {!isAuthed && (
              <p className="mt-3 text-center text-xs text-muted-foreground">
                <Link to="/signup" className="font-medium text-primary hover:underline">{t("od.create_account")}</Link>{t("od.signup_rest")}
              </p>
            )}
          </div>
        </aside>
      </div>

      {offering.related?.length ? (
        <section className="mt-16">
          <h2 className="mb-6 font-display text-2xl text-foreground">{t("od.more_from", { name: coach.name })}</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {offering.related.map((o) => <OfferingCard key={o.id} offering={o} />)}
          </div>
        </section>
      ) : null}
    </div>
  );
}
